
// src/app/shared/services/csv-export.service.ts
import { Injectable } from '@angular/core';

export interface ColumnDef<T = any> {
  /** Property path on the row (supports deep paths like 'address.city') */
  field: string;
  /** CSV header label for this column */
  header: string;
  /** Optional formatter to compute/format the value for this column */
  formatter?: (value: any, row: T) => any;
}

export interface CsvSection<T = any> {
  /** Optional section title (not required for CSV; purely cosmetic) */
  title?: string;
  /** The dataset to export */
  data: T[];
  /** Column definitions for this dataset */
  columns: ColumnDef<T>[];
}

export interface CsvExportOptions {
  /** Column separator; Excel-friendly default is comma */
  delimiter?: string;               // default: ','
  /** Add UTF-8 BOM so Excel opens UTF-8 correctly */
  includeBom?: boolean;             // default: true
  /** Line ending */
  lineEnd?: '\r\n' | '\n';          // default: '\r\n'
  /** Placeholder for null/undefined values */
  nullValue?: string;               // default: ''
  /** Formatter for Date values when no custom formatter is supplied */
  dateFormatter?: (d: Date) => string; // default: ISO-like
  /** Insert an empty row between sections */
  addSectionSeparatorRow?: boolean; // default: true
}

@Injectable({ providedIn: 'root' })
export class CsvExporter {
  private defaultOptions: Required<CsvExportOptions> = {
    delimiter: ',',
    includeBom: true,
    lineEnd: '\r\n',
    nullValue: '',
    dateFormatter: (d: Date) => {
      // yyyy-MM-dd HH:mm:ss
      const pad = (n: number) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} `
           + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },
    addSectionSeparatorRow: true,
  };

  /** Public API to export multiple datasets as one CSV file */
  exportMultipleSections<T = any>(
    sections: CsvSection<T>[],
    fileName = 'export.csv',
    opts?: CsvExportOptions
  ): void {
    const options = { ...this.defaultOptions, ...(opts || {}) };

    const allLines: string[] = [];

    sections.forEach((section, idx) => {
      // Optional title row (commented out by default — uncomment if you want titles in CSV)
      // if (section.title) {
      //   allLines.push(this.escapeCsv(section.title, options.delimiter));
      // }

      // Header row
      const headers = section.columns.map(c => this.escapeCsv(c.header, options.delimiter));
      allLines.push(headers.join(options.delimiter));

      // Data rows
      for (const row of section.data || []) {
        const cells = section.columns.map(col => {
          const raw = this.getCellValue(row, col);
          const formatted = this.formatValue(raw, options);
          return this.escapeCsv(formatted, options.delimiter);
        });
        allLines.push(cells.join(options.delimiter));
      }

      // Blank line between sections (except after last one)
      if (options.addSectionSeparatorRow && idx < sections.length - 1) {
        allLines.push(''); // empty line
      }
    });

    const csvContent = (options.includeBom ? '\uFEFF' : '') + allLines.join(options.lineEnd);
    this.download(csvContent, fileName);
  }

  // ---- helpers -------------------------------------------------------------

  /** Get value from row using formatter or dotted path */
  private getCellValue<T>(row: T, col: ColumnDef<T>): any {
    if (typeof col.formatter === 'function') {
      const base = this.getByPath(row as any, col.field);
      return col.formatter(base, row);
    }
    return this.getByPath(row as any, col.field);
  }

  /** Get nested value by path like "a.b.c" */
  private getByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
  }

  /** Normalize and stringify for CSV */
  private formatValue(value: any, options: Required<CsvExportOptions>): string {
    if (value === null || value === undefined) return options.nullValue;

    // Dates
    if (value instanceof Date) {
      return options.dateFormatter(value);
    }

    // If stringifiable primitive
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    // Arrays -> pipe-joined
    if (Array.isArray(value)) {
      return value.map(v => (v instanceof Date ? options.dateFormatter(v) : String(v))).join(' | ');
    }

    // Objects -> JSON
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  /** Escape per RFC 4180: wrap in quotes if needed; double any embedded quotes */
  private escapeCsv(val: string, delimiter: string): string {
    if (val == null) return '';
    const needsQuotes = val.includes(delimiter) || val.includes('"') || /\r|\n/.test(val);
    return needsQuotes ? `"${val.replace(/"/g, '""')}"` : val;
  }

  /** Trigger browser download */
  private download(content: string, fileName: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}
